/**
 * Kameleoon Goal Id  constants
 */
const KameleoonGoals = {
    AccountCreated: 'accountCreated',
    AskQuestionClickedItemPage: 'askQuestionClickedItemPage',
    AuctionHouseClickedPriceResults: 'auctionHouseClickedPriceResults',
    BidPlaced: 'absenteeBidPlaced',
    BidPlacedItemPage: 'bidPlacedItemPage',
    BuyNowButtonPressed: 'buyNowButtonPressed',
    BuyNowHeaderLinkClicked: 'buyNowHeaderLinkClicked',
    BuyNowOfferSubmitted: 'buyNowOfferSubmitted',
    CategoryInNavClicked: 'categoryInNavClicked',
    CheckoutPayNowClicked: 'checkoutPayNowClicked',
    CompleteAccountSetupClicked: 'completeAccountSetupClicked',
    FollowedPriceResultsSearch: 'followedPriceResultsSearch',
    ForYouLoggedInButtonClicked: 'forYouButtonClicked',
    GetAppBannerClicked: 'getAppBannerClicked',
    ItemCardClicked: 'itemCardClicked',
    ItemCardClickedCatalogPage: 'itemCardClickedCatalogPage',
    J52BidPlaced: 'j52BidPlaced',
    LearnMoreClickedPurchaseProtectionBlurb: 'learnMoreClickedPurchaseProtectionBlurb',
    MainItemClickedPriceResults: 'mainItemClickedPriceResults',
    MessagedSellerItemPage: 'messagedSellerItemPage',
    RegisterForAuction: 'registerForAuction',
    RegisterForAuctionItemPage: 'registerForAuctionItemPage',
    ReturnBidPlaced: 'returnBidPlaced',
    ReviewsModalViewedPriceResults: 'ReviewsModalViewedPriceResults',
    SavedItem: 'savedItem',
    SavedItemCatalogPage: 'savedItemCatalogPage',
    SavedItemItemPage: 'savedItemItemPage',
    SavedPriceResultsItem: 'savedPriceResultsItem',
    SavedSearchItem: 'savedSearchItem',
    SearchBarHouseClicked: 'searchBarHouseClicked',
    SearchBarSearchSubmitted: 'searchBarSearchSubmitted',
    SearchBarSuggestionClicked: 'searchBarSuggestionClicked',
    SearchFiltersClicked: 'searchFiltersClicked',
    SearchResultsAdUnitClicked: 'searchResultsAdUnitClicked',
    Under100ExperimentBidPlaced: 'Under100ExperimentBidPlaced',
    WonItemsLoggedInButtonClicked: 'wonItemsButtonClicked',
};

export type KameleoonGoal = {
    goalId: number;
    goalName: string;
};

/**
 * @field AskQuestionClickedItemPage - The Ask Question button was clicked on the item page
 * @field BidPlaced - A bid has been placed (absentee and live bids should both be counted)
 * @field BidPlacedItemPage - A bid has been placed from the item page (absentee/timed+ bids)
 * @field BuyNowButtonPressed - The Buy Now button was clicked from the item page
 * @field BuyNowHeaderLinkClicked - The Buy Now header link was clicked
 * @field BuyNowOfferSubmitted - The Buy Now offer that differs from sellers price was submitted
 * @field GetAppBannerClicked - The Get App button was clicked in the mobile banner
 * @field ItemCardClicked - The card, in search, has been clicked
 * @field ItemCardClickedCatalog - A item card was clicked from the catalog page
 * @field LearnMoreClickedPurchaseProtectionBlurb - The user has clicked the learn more link in the purchase protection blurb
 * @field MessagedSellerItemPage - A message to the seller has been sent from the item page
 * @field ReturnBidPlaced - The user has placed a bid on an item they have already bid on
 * @field SavedItem - The item has been favorited
 * @field SavedItemCatalogPage - The item has been favorited from the catalog page
 * @field SavedItemCategoryPage - The item has been favorited from the category page
 * @field SavedItemItemPage - The item has been favorited from the item page
 * @field SavedPriceResultsItem - The item has been favorited from the price results page
 * @field SavedSearchItem - The item has been favorited from the search page
 * @field RegisterAccount - The user has registered an account
 * @field RegisterForAuctionItemPage - The user has registered for an auction from the item page
 * @field SavedPriceResultsItem - The item has been favorited from the price results page
 * @field SavedSearchItem - The item has been favorited from the search page
 * @field FollowedPriceResultsSearch - The user has followed the price results from the search page
 * @field MainItemClickedPriceResults - The main item, in price results, has been clicked
 * @field J52BidPlaced - A bid has been placed in the J52 Buyers Premium experiment
 * @field AuctionHouseClickedPriceResults - The auction house has been clicked in the price results page
 * @field ReviewsModalViewedPriceResults - The reviews modal has been viewed in the price results page
 * @field SearchBarHouseClicked - The search bar house has been clicked
 * @field SearchBarSuggestionClicked - The search bar suggestion has been clicked
 * @field SearchBarSearchSubmitted - The search bar search has been submitted
 * @field CategoryInNavClicked - The category in the navigation has been clicked
 * @field SearchResultsAdUnitClicked - The ad unit in the search results has been clicked
 * @field CompleteAccountSetupClicked - The complete account setup button was clicked
 * @field Under100ExperimentBidPlaced - A bid has been placed in the Under 100 Experiment
 * @field SearchFiltersClicked - The search filters have been clicked
 */
export const KameleoonGoalData: {
    [key in keyof typeof KameleoonGoals]: KameleoonGoal;
} = {
    AccountCreated: { goalId: 356083, goalName: 'AccountCreated' },
    AskQuestionClickedItemPage: { goalId: 355032, goalName: 'AskQuestionClickedItemPage' },
    AuctionHouseClickedPriceResults: { goalId: 361327, goalName: 'AuctionHouseClickedPriceResults' },
    BidPlaced: { goalId: 352421, goalName: 'BidPlaced' },
    BidPlacedItemPage: { goalId: 352904, goalName: 'BidPlacedItemPage' },
    BuyNowButtonPressed: { goalId: 359033, goalName: 'BuyNowButtonPressed' },
    BuyNowHeaderLinkClicked: { goalId: 361192, goalName: 'BuyNowHeaderLinkClicked' },
    BuyNowOfferSubmitted: { goalId: 359037, goalName: 'BuyNowOfferSubmitted' },
    CategoryInNavClicked: { goalId: 362560, goalName: 'CategoryInNavClicked' },
    CheckoutPayNowClicked: { goalId: 373537, goalName: 'CheckoutPayNowClicked' },
    CompleteAccountSetupClicked: { goalId: 370272, goalName: 'CompleteAccountSetupClicked' },
    FollowedPriceResultsSearch: { goalId: 356084, goalName: 'FollowedPriceResultsSearch' },
    ForYouLoggedInButtonClicked: { goalId: 372893, goalName: 'ForYouLoggedInButtonClicked' },
    GetAppBannerClicked: { goalId: 375869, goalName: 'GetAppBannerClicked' },
    ItemCardClicked: { goalId: 353263, goalName: 'ItemCardClicked' },
    ItemCardClickedCatalogPage: { goalId: 361323, goalName: 'ItemCardClickedCatalog' },
    J52BidPlaced: { goalId: 357726, goalName: 'J52BidPlaced' },
    LearnMoreClickedPurchaseProtectionBlurb: { goalId: 367454, goalName: 'LearnMoreClickedPurchaseProtectionBlurb' },
    MainItemClickedPriceResults: { goalId: 356096, goalName: 'MainItemClickedPriceResults' },
    MessagedSellerItemPage: { goalId: 353604, goalName: 'MessagedSellerItemPage' },
    RegisterForAuction: { goalId: 356085, goalName: 'RegisterForAuction' },
    RegisterForAuctionItemPage: { goalId: 353268, goalName: 'RegisterForAuctionItemPage' },
    ReturnBidPlaced: { goalId: 375767, goalName: 'ReturnBidPlaced' },
    ReviewsModalViewedPriceResults: { goalId: 361324, goalName: 'ReviewsModalViewedPriceResults' },
    SavedItem: { goalId: 352422, goalName: 'SavedItem' },
    SavedItemCatalogPage: { goalId: 358778, goalName: 'SavedItemCatalogPage' },
    SavedItemItemPage: { goalId: 353259, goalName: 'SavedItemItemPage' },
    SavedPriceResultsItem: { goalId: 356094, goalName: 'SavedPriceResultsItem' },
    SavedSearchItem: { goalId: 353262, goalName: 'SavedSearchItem' },
    SearchBarHouseClicked: { goalId: 361529, goalName: 'SearchBarHouseClicked' },
    SearchBarSearchSubmitted: { goalId: 361527, goalName: 'SearchBarSearchSubmitted' },
    SearchBarSuggestionClicked: { goalId: 361528, goalName: 'SearchBarSuggestionClicked' },
    SearchFiltersClicked: { goalId: 364326, goalName: 'SearchFilterClicked' },
    SearchResultsAdUnitClicked: { goalId: 366540, goalName: 'SearchResultsAdUnitClicked' },
    Under100ExperimentBidPlaced: { goalId: 374035, goalName: 'Under100ExperimentBidPlaced' },
    WonItemsLoggedInButtonClicked: { goalId: 372894, goalName: 'WonItemsLoggedInButtonClicked' },
};

export type KameleoonGoalKeys = (typeof KameleoonGoals)[keyof typeof KameleoonGoals];
